(function () {
  'use strict';

  const PREFETCH_OFFSET_FROM_END = 4;
  const LOAD_MORE_REMAINING_THRESHOLD = 2;

  function safeJSONParse(s, fallback) {
    if (!s) return fallback;
    try { return JSON.parse(s); } catch (_) { return fallback; }
  }

  function buildSrcset(img, img2x) {
    if (!img) return '';
    const parts = [img + ' 1x'];
    if (img2x) parts.push(img2x + ' 2x');
    return parts.join(', ');
  }

  function applyColor(tile, colorName) {
    const colors = safeJSONParse(tile.dataset.colors, []);
    if (!colors.length) return;

    let target = colors.find(function (c) { return c.name === colorName; });
    if (!target) target = colors[0];

    const img = tile.querySelector('[data-card-img]');
    if (img && target.img) {
      img.src = target.img;
      img.srcset = buildSrcset(target.img, target.img2x);
      img.removeAttribute('sizes');
    }

    if (target.url) {
      tile.querySelectorAll('[data-card-link]').forEach(function (a) { a.href = target.url; });
    }

    tile.querySelectorAll('[data-swatch]').forEach(function (sw) {
      sw.classList.toggle('swatch--active', sw.dataset.colorName === target.name);
    });

    tile.dataset.activeColor = target.name;
  }

  function bindSwatches(tile) {
    if (tile.dataset.swatchesBound === '1') return;
    tile.dataset.swatchesBound = '1';

    const swatches = tile.querySelectorAll('[data-swatch]');
    swatches.forEach(function (sw) {
      sw.addEventListener('mouseenter', function () { applyColor(tile, sw.dataset.colorName); });
      sw.addEventListener('focus', function () { applyColor(tile, sw.dataset.colorName); });
      sw.addEventListener('click', function () { applyColor(tile, sw.dataset.colorName); });
    });
  }

  function bindAllSwatches(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.product-card-v2').forEach(bindSwatches);
  }

  class CarouselController {
    constructor() {
      this.cache = new Map();
    }

    attach(carouselEl) {
      if (carouselEl.dataset.controllerAttached === '1') return;
      carouselEl.dataset.controllerAttached = '1';

      if (typeof Swiper === 'undefined') {
        console.warn('[carousel-controller] Swiper not loaded, retrying.');
        const retry = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(retry);
            this.initSwiper(carouselEl);
          }
        }, 100);
        return;
      }
      this.initSwiper(carouselEl);
    }

    initSwiper(carouselEl) {
      const viewport = carouselEl.querySelector('.swiper');
      if (!viewport) return;

      const prevEl = carouselEl.querySelector('.type-carousel__prev');
      const nextEl = carouselEl.querySelector('.type-carousel__next');
      const paginationEl = carouselEl.querySelector('.type-carousel__pagination');

      const slidesPerViewMobile = parseFloat(carouselEl.dataset.slidesMobile) || 1.3;
      const slidesPerViewTablet = parseFloat(carouselEl.dataset.slidesTablet) || 2.3;
      const slidesPerViewDesktop = parseFloat(carouselEl.dataset.slidesDesktop) || 4;

      bindAllSwatches(carouselEl);

      const swiper = new Swiper(viewport, {
        slidesPerView: slidesPerViewMobile,
        slidesPerGroup: 1,
        spaceBetween: 15,
        speed: 400,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        simulateTouch: true,
        allowTouchMove: true,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true
        },
        navigation: prevEl && nextEl ? { prevEl: prevEl, nextEl: nextEl } : false,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
        breakpoints: {
          750: { slidesPerView: slidesPerViewTablet, slidesPerGroup: 1, spaceBetween: 16 },
          990: { slidesPerView: slidesPerViewDesktop, slidesPerGroup: 1, spaceBetween: 20 }
        }
      });

      carouselEl._swiper = swiper;

      const totalSlides = swiper.slides.length;
      if (totalSlides < parseInt(carouselEl.dataset.pageSize, 10)) {
        carouselEl.dataset.exhausted = '1';
      }

      swiper.on('slideChangeTransitionEnd', () => this.maybeLoadMore(carouselEl, swiper));

      this.observePrefetch(carouselEl, swiper);

      this.updateNavState(carouselEl, swiper);
      swiper.on('slideChange', () => this.updateNavState(carouselEl, swiper));
      swiper.on('reachEnd', () => this.updateNavState(carouselEl, swiper));
      swiper.on('reachBeginning', () => this.updateNavState(carouselEl, swiper));
    }

    updateNavState(carouselEl, swiper) {
      const prevEl = carouselEl.querySelector('.type-carousel__prev');
      const nextEl = carouselEl.querySelector('.type-carousel__next');
      if (prevEl) prevEl.disabled = swiper.isBeginning;
      if (nextEl) nextEl.disabled = (swiper.isEnd && carouselEl.dataset.exhausted === '1');
    }

    observePrefetch(carouselEl, swiper) {
      const slides = swiper.slides;
      if (!slides || !slides.length) return;
      const targetIdx = Math.max(0, slides.length - PREFETCH_OFFSET_FROM_END);
      const target = slides[targetIdx];
      if (!target) return;

      const observer = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) this.prefetch(carouselEl);
      }, { root: carouselEl.querySelector('.swiper'), threshold: 0.5 });
      observer.observe(target);
    }

    cacheKey(handle, page) { return handle + ':' + page; }

    fetchPage(handle, page) {
      const url = '/collections/' + handle + '?page=' + page + '&section_id=carousel-tiles';
      return fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('Fetch failed: ' + res.status);
        return res.text();
      }).then(function (html) {
        const dom = new DOMParser().parseFromString(html, 'text/html');
        const tpl = dom.querySelector('template[data-carousel-tiles-source]');
        if (!tpl) return [];
        const tiles = tpl.content.querySelectorAll('[data-carousel-tile]');
        return Array.from(tiles);
      });
    }

    prefetch(carouselEl) {
      if (carouselEl.dataset.exhausted === '1') return;
      const handle = carouselEl.dataset.collectionHandle;
      const page = parseInt(carouselEl.dataset.nextPage, 10);
      if (!handle || !page) return;
      const key = this.cacheKey(handle, page);
      if (!this.cache.has(key)) this.cache.set(key, this.fetchPage(handle, page));
    }

    maybeLoadMore(carouselEl, swiper) {
      if (carouselEl.dataset.exhausted === '1') return;
      const remaining = swiper.slides.length - swiper.activeIndex - swiper.params.slidesPerView;
      if (remaining <= LOAD_MORE_REMAINING_THRESHOLD) this.loadNext(carouselEl, swiper);
    }

    loadNext(carouselEl, swiper) {
      if (carouselEl.dataset.loadingMore === '1') return;
      if (carouselEl.dataset.exhausted === '1') return;
      const handle = carouselEl.dataset.collectionHandle;
      const page = parseInt(carouselEl.dataset.nextPage, 10);
      if (!handle || !page) return;

      carouselEl.dataset.loadingMore = '1';
      const key = this.cacheKey(handle, page);
      if (!this.cache.has(key)) this.cache.set(key, this.fetchPage(handle, page));

      const pageSize = parseInt(carouselEl.dataset.pageSize, 10) || 5;

      this.cache.get(key).then((tiles) => {
        carouselEl.dataset.loadingMore = '0';
        if (!tiles.length) {
          carouselEl.dataset.exhausted = '1';
          this.updateNavState(carouselEl, swiper);
          return;
        }
        tiles.forEach(function (t) { swiper.appendSlide(t.outerHTML); });
        bindAllSwatches(carouselEl);
        carouselEl.dataset.nextPage = page + 1;
        if (tiles.length < pageSize) carouselEl.dataset.exhausted = '1';
        swiper.update();
        this.updateNavState(carouselEl, swiper);
      }).catch((err) => {
        carouselEl.dataset.loadingMore = '0';
        console.error('[carousel-controller] load more failed:', err);
      });
    }
  }

  const controller = new CarouselController();

  function attachAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-type-carousel]').forEach(function (el) {
      controller.attach(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { attachAll(document); });
  } else {
    attachAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { attachAll(e.target); });
  document.addEventListener('theme:product-grid:rendered', function (e) {
    attachAll(e.detail && e.detail.container ? e.detail.container : document);
  });
})();
